import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) {
    throw new Error(body?.message || 'AI governance request failed');
  }
  return body.data;
}

export type AiGovernanceDashboard = {
  predictions_90d: number;
  accuracy_pct: number | null;
  validation: {
    correct: number;
    partially_correct: number;
    incorrect: number;
    scored: number;
  };
  avg_trust: number | null;
  trust_distribution: {
    very_high: number;
    high: number;
    moderate: number;
    limited: number;
    low: number;
  };
  feedback: { helpful: number; not_helpful: number; total: number };
  drift_events_90d: number;
  briefs_captured_90d: number;
  refreshed_at: string;
};

export const aiGovernanceService = {
  getDashboard: () => unwrap<AiGovernanceDashboard>(api.get('/ai-governance/dashboard')),
  getTrust: (limit = 50) => unwrap<unknown[]>(api.get(`/ai-governance/trust?limit=${limit}`)),
  getPredictions: (params?: { entityType?: string; entityId?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.entityType) q.set('entity_type', params.entityType);
    if (params?.entityId) q.set('entity_id', params.entityId);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return unwrap<unknown[]>(api.get(`/ai-governance/predictions${qs ? `?${qs}` : ''}`));
  },
  getAnalyzers: () =>
    unwrap<
      Array<{
        analyzer_key: string;
        version: string;
        status: string;
        description?: string;
        prediction_count_90d: number;
        avg_confidence: number | null;
        avg_trust: number | null;
      }>
    >(api.get('/ai-governance/analyzers')),
  getOutcomes: (limit = 50) => unwrap<unknown[]>(api.get(`/ai-governance/outcomes?limit=${limit}`)),
  getDrift: (limit = 50) => unwrap<unknown[]>(api.get(`/ai-governance/drift?limit=${limit}`)),
  getBenchmarks: () =>
    unwrap<{
      current_90d: { scored: number; accuracy_pct: number | null };
      prior_90d: { scored: number; accuracy_pct: number | null };
      refreshed_at: string;
    }>(api.get('/ai-governance/benchmarks')),
  getAudit: (entityType: string, entityId: string) =>
    unwrap<unknown>(api.get(`/ai-governance/audit/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`)),
  submitFeedback: (payload: {
    entityType: string;
    entityId: string;
    contentHash?: string;
    rating: 'helpful' | 'not_helpful';
    reasonCodes?: string[];
    comment?: string;
  }) =>
    unwrap<{ id: string }>(
      api.post('/ai-governance/feedback', {
        entityType: payload.entityType,
        entityId: payload.entityId,
        contentHash: payload.contentHash,
        rating: payload.rating,
        reasonCodes: payload.reasonCodes,
        comment: payload.comment,
      })
    ),
  registerOutcome: (payload: {
    entityType: string;
    entityId: string;
    outcomeCode: string;
    source?: string;
    detail?: unknown;
  }) => unwrap<{ outcomeId: string }>(api.post('/ai-governance/outcomes', payload)),
};
